import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

import { useAppDispatch } from 'src/app/store/hooks';
import { DeleteModal } from 'src/common/DeleteModal';
import {
  RouteTemplateRead,
  useRoutesRouteTemplatesDestroyMutation,
} from 'src/common/external/bambi-api/routesApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { useEditRouteTemplateModal } from '../hooks/useEditRouteTemplateModal';
import { useLazyHydrateRouteTemplateData } from '../hooks/useLazyHydrateRouteTemplateData';

interface ManageCellProps {
  routeTemplate: RouteTemplateRead;
}
export function ManageCell({ routeTemplate }: ManageCellProps) {
  const dispatch = useAppDispatch();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { editRouteTemplateModal } = useEditRouteTemplateModal();
  const [triggerDelete, { isLoading: isDeleting }] =
    useRoutesRouteTemplatesDestroyMutation();

  const { fetchRouteTemplateData, isLoading: isFetchingRouteTemplateData } =
    useLazyHydrateRouteTemplateData();

  const handleEdit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      try {
        const routeTemplateData = await fetchRouteTemplateData(
          routeTemplate.id
        );
        editRouteTemplateModal(routeTemplateData);
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to edit route template',
            description: formatServerError(e),
          })
        );
      }
    },
    [dispatch, editRouteTemplateModal, fetchRouteTemplateData, routeTemplate]
  );

  const performDelete = useCallback(async () => {
    setIsConfirmingDelete(false);
    try {
      await triggerDelete({
        id: routeTemplate.id,
      }).unwrap();
      dispatch(
        show({
          title: 'Successfully deleted route template',
          type: 'success',
        })
      );
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to delete route template',
          description: formatServerError(e),
        })
      );
    }
  }, [dispatch, routeTemplate.id, triggerDelete]);

  return (
    <div className="flex flex-row gap-2">
      <Button
        variant="icon"
        onClick={handleEdit}
        ariaLabel={`Edit route template: ${routeTemplate.name}`}
        disabled={isFetchingRouteTemplateData}
        loading={isFetchingRouteTemplateData}
        dataTestId={`edit-route-template-${routeTemplate.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <PencilIcon className="w-[24px]" />
          Edit
        </div>
      </Button>
      <Button
        variant="icon"
        onClick={(e) => {
          e.stopPropagation();
          setIsConfirmingDelete(true);
        }}
        ariaLabel={`Delete route template: ${routeTemplate.name}`}
        disabled={isDeleting}
        loading={isDeleting}
        dataTestId={`delete-route-template-${routeTemplate.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <TrashIcon className="w-[24px] stroke-red-600" />
          Delete
        </div>
      </Button>
      <DeleteModal
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
        onCancel={() => setIsConfirmingDelete(false)}
        onConfirm={performDelete}
        title={`Delete route template: ${routeTemplate.name}`}
        description={`Are you sure you want to delete this route template? This action cannot be undone.`}
      />
    </div>
  );
}
